import { z } from 'zod';
import { noyauCodeSchema } from './noyau.js';

const noyauxChoisisSchema = z
  .array(noyauCodeSchema)
  .min(1, 'Veuillez choisir au moins un noyau cible')
  .refine(
    (noyaux) => new Set(noyaux).size === noyaux.length,
    'Un noyau ne peut être choisi deux fois',
  );

/**
 * The longest text Rosetta translates (#13): 10 000 characters, spaces and line breaks included,
 * about three A4 pages in a 12 pt font. Beyond it the translation is refused with a clear message,
 * and a file whose text is longer is not loaded (nothing is ever cut silently).
 */
export const LONGUEUR_MAX_TEXTE = 10_000;

/**
 * The length of a text as the user sees it: in characters (Unicode code points), so that a
 * character written with two UTF-16 units (an emoji) counts once. Linear, with no array built:
 * it runs at every keystroke, on texts that can be pasted far beyond the limit.
 */
export function longueurTexte(texte: string): number {
  let longueur = texte.length;
  for (let index = 0; index < texte.length; index++) {
    const code = texte.charCodeAt(index);
    // The first half of a surrogate pair followed by its second half: one character.
    if (code >= 0xd800 && code <= 0xdbff) {
      const suivant = texte.charCodeAt(index + 1);
      if (suivant >= 0xdc00 && suivant <= 0xdfff) {
        longueur -= 1;
        index += 1;
      }
    }
  }
  return longueur;
}

export const MESSAGE_TEXTE_TROP_LONG = 'Le texte dépasse 10 000 caractères';

export const demandeTraductionSchema = z.object({
  titre: z.string().optional(),
  texte: z
    .string()
    .refine((texte) => texte.trim().length > 0, 'Veuillez saisir ou charger un texte')
    .refine((texte) => longueurTexte(texte) <= LONGUEUR_MAX_TEXTE, MESSAGE_TEXTE_TROP_LONG),
  noyaux: noyauxChoisisSchema,
});
export type DemandeTraduction = z.infer<typeof demandeTraductionSchema>;

/**
 * The state of a translation, as the screens show it:
 *  - `en_cours`: Tahiti is working on it;
 *  - `bloquee` (#13): Tahiti still says it runs, but one of its steps failed (I, F or C): it will
 *    not end on its own, the screens offer "interrompre" and "relancer" and stop reading it;
 *  - `terminee`: every chosen noyau has its translation;
 *  - `partielle` (#13): it ended with the translation of some noyaux only, the others did not
 *    succeed;
 *  - `echouee`: it did not succeed (Tahiti statuses I and F, a finished task with no translation
 *    to read, or a task never started, #13);
 *  - `interrompue`: it was cancelled (status C, #7): the user stopped it, or it was stopped
 *    elsewhere.
 */
export const statutTraductionSchema = z.enum([
  'en_cours',
  'bloquee',
  'terminee',
  'partielle',
  'echouee',
  'interrompue',
]);
export type StatutTraduction = z.infer<typeof statutTraductionSchema>;

export const resultatNoyauSchema = z.object({
  noyau: noyauCodeSchema,
  statut: statutTraductionSchema,
  texte: z.string().optional(),
});
export type ResultatNoyau = z.infer<typeof resultatNoyauSchema>;

/** The steps of a running translation (#7), in the order Rosetta v5 chains them. */
export const codeEtapeSchema = z.enum(['analyse', 'invariants', 'traduction', 'controle']);
export type CodeEtape = z.infer<typeof codeEtapeSchema>;

/** `echec` (#13): the sub-task of the step failed or was cancelled while the translation runs. */
export const etatEtapeSchema = z.enum(['attente', 'en_cours', 'terminee', 'echec']);
export type EtatEtape = z.infer<typeof etatEtapeSchema>;

/**
 * One line of the waiting screen: `code` says which step it is, `libelle` is what the screen
 * shows ("Analyse du texte", "Repérage des invariants", "Traduction SGP"), `noyau` is set for
 * the translation of one noyau and `dureeSecondes` for a finished step.
 */
export const etapeTraductionSchema = z.object({
  code: codeEtapeSchema,
  libelle: z.string().min(1),
  noyau: noyauCodeSchema.optional(),
  etat: etatEtapeSchema,
  dureeSecondes: z.number().int().nonnegative().optional(),
});
export type EtapeTraduction = z.infer<typeof etapeTraductionSchema>;

/**
 * A noyau of the text and the launch (the task) of its document that carries it (#36): for a noyau
 * being translated, the launch whose waiting screen its link opens; for a noyau already translated
 * (the "Voir aussi" of the waiting screen), the launch whose result its link opens.
 */
export const noyauLancementSchema = z.object({
  noyau: noyauCodeSchema,
  lancement: z.string().min(1),
});
export type NoyauLancement = z.infer<typeof noyauLancementSchema>;

/**
 * A noyau of the text whose translation is running in another launch of the same document (#36):
 * the result and the analysis list it with a spinner, and its link opens the waiting screen of
 * `lancement`, the identifier of that launch.
 */
export const noyauEnCoursSchema = noyauLancementSchema;
export type NoyauEnCours = NoyauLancement;

export const traductionSchema = z.object({
  id: z.string().min(1),
  titre: z.string().optional(),
  texteOrigine: z.string(),
  noyaux: noyauxChoisisSchema,
  statut: statutTraductionSchema,
  resultats: z.array(resultatNoyauSchema),
  /**
   * While it runs (#7): its steps, when Tahiti lets the client read them (absent otherwise: the
   * waiting screen then only says "en cours"), and when it started (ISO 8601 with a UTC offset).
   */
  etapes: z.array(etapeTraductionSchema).optional(),
  /**
   * Once the translation can be read (finished or partial, #36): the noyaux of its document that
   * another launch is translating right now, in the charter order, each with that launch. A launch
   * that is blocked, failed or interrupted lists none; a noyau whose translation is done but whose
   * launch still runs (the quality control is its last step) is listed too, and is no tab. Absent
   * when there is none.
   */
  noyauxEnCours: z.array(noyauEnCoursSchema).optional(),
  /**
   * While it runs (#36, the waiting screen W1): the noyaux of its document already translated by
   * other launches, which are not its own, each with the launch whose result carries it, in the
   * charter order: the "Voir aussi :" of the waiting screen. Absent when there is none.
   */
  autresTraductions: z.array(noyauLancementSchema).optional(),
  commenceeLe: z.string().datetime({ offset: true }).optional(),
  /**
   * The error message of Tahiti (`error_message` of the task), as it is, when it is readable
   * (#13): shown on a failed, partial or blocked translation. Absent when Tahiti gave none, or
   * one that reads like a technical dump.
   */
  messageErreur: z.string().min(1).optional(),
  /**
   * A failed translation whose task was created and never started (#13: status W for longer
   * than the client's `DELAI_DEMARRAGE_MS`): no treatment ran, nothing was paid.
   */
  nonDemarree: z.literal(true).optional(),
});
export type Traduction = z.infer<typeof traductionSchema>;

/**
 * One line of the history: what a card shows. A failed translation is there too (#5), with
 * the status `echouee`: its card offers "relancer" and "écarter". A blocked one (#13) offers
 * "relancer" and "interrompre", a partial one opens its result like a finished one.
 */
export const resumeTraductionSchema = z.object({
  id: z.string().min(1),
  titre: z.string().optional(),
  texteOrigine: z.string(),
  noyaux: noyauxChoisisSchema,
  statut: statutTraductionSchema,
  /** Creation instant, ISO 8601 with a UTC offset. */
  creeLe: z.string().datetime({ offset: true }),
  /**
   * The source document of the translation (#11): a text translated towards another noyau later
   * is a new translation of the same document, and the history shows the translations of one
   * document as one card. Absent, the translation is a card of its own.
   */
  documentId: z.string().min(1).optional(),
});
export type ResumeTraduction = z.infer<typeof resumeTraductionSchema>;

/**
 * Where one noyau stands for the text of a translation (#11), whichever translation of that text
 * did the work:
 *  - `traduit`: a translation of the text towards it has ended well, its result can be read;
 *  - `en_cours`: a translation towards it is running;
 *  - `interrompu`: the last translation towards it was cancelled (Tahiti status C);
 *  - `echoue`: the last translation towards it did not succeed;
 *  - `libre`: the text was never translated towards it.
 * Only `traduit` and `en_cours` keep the noyau from being added again: an interrupted or failed
 * one can be tried again.
 */
export const etatNoyauTexteSchema = z.enum([
  'traduit',
  'en_cours',
  'interrompu',
  'echoue',
  'libre',
]);
export type EtatNoyauTexte = z.infer<typeof etatNoyauTexteSchema>;

export const noyauTexteSchema = z.object({
  noyau: noyauCodeSchema,
  etat: etatNoyauTexteSchema,
});
export type NoyauTexte = z.infer<typeof noyauTexteSchema>;

/** The six noyaux of a text (#11), in the order of the charter, each once. */
export const noyauxTexteSchema = z
  .array(noyauTexteSchema)
  .length(6)
  .refine(
    (noyaux) => new Set(noyaux.map((ligne) => ligne.noyau)).size === noyaux.length,
    'Un noyau ne peut figurer deux fois',
  );
export type NoyauxTexte = z.infer<typeof noyauxTexteSchema>;

/**
 * Translating the text of a translation towards other noyaux (#11): the noyaux to add, at least one
 * and each once. The text, its title and its document are the ones of the translation.
 */
export const demandeAjoutNoyauxSchema = z.object({
  noyaux: noyauxChoisisSchema,
});
export type DemandeAjoutNoyaux = z.infer<typeof demandeAjoutNoyauxSchema>;
